import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, UserPlus } from 'lucide-react'
import {
  SearchableComboboxShell,
  type SearchableComboboxOption,
} from '@/components/ui/searchable-combobox-shell'
import { Button } from '@/components/ui/button'
import { PortageContactSuggestDialog } from '@/components/ui/portage-contact-suggest-dialog'
import { usePortageContactOptions } from '@/hooks/queries/usePortageCompanies'
import { formatPortageContactLabel } from '@/services/portageService'

interface PortageContactComboboxProps {
  portageCompanyId: string | null
  value: string | null
  onChange: (contactId: string | null, label?: string | null, status?: string | null) => void
  disabled?: boolean
  className?: string
  error?: string
  /** Libellé de repli quand le contact n'est pas dans la liste verified (ex. pending). */
  selectedLabel?: string | null
  /** Statut du contact sélectionné (ex. pending) pour afficher un badge. */
  selectedStatus?: string | null
  /** Affiche le bouton d'ajout en bas du menu (défaut true). */
  allowAddContact?: boolean
  /** Wording du dialogue d'ajout : freelance (suggestion) ou équipe (création mission). */
  addContactVariant?: 'freelancer' | 'team'
  /** Nom société (dialogue interne) — utilisé si le combobox gère le dialogue. */
  portageCompanyName?: string | null
  /**
   * Si fourni, le pied de liste appelle ce callback (le parent gère le dialogue).
   * Sinon le combobox ouvre son propre PortageContactSuggestDialog.
   */
  onAddContactClick?: () => void
}

interface ContactOption extends SearchableComboboxOption {
  label: string
  email: string
  status: string
}

/**
 * Sélecteur de responsable de compte pour une société de portage
 * (onboarding freelance + création mission côté équipe).
 */
export function PortageContactCombobox({
  portageCompanyId,
  value,
  onChange,
  disabled = false,
  className,
  error,
  selectedLabel,
  allowAddContact = true,
  addContactVariant = 'freelancer',
  portageCompanyName,
  onAddContactClick,
}: PortageContactComboboxProps) {
  const { t } = useTranslation()
  const [isSuggestOpen, setIsSuggestOpen] = useState(false)
  const ownsDialog = !onAddContactClick
  const footerLabel =
    addContactVariant === 'freelancer'
      ? t('freelancerOnboarding.portageContact.suggestMine')
      : t('freelancerOnboarding.portageContact.addContact')
  const FooterIcon = addContactVariant === 'freelancer' ? UserPlus : Plus

  const { data: contacts = [], isLoading } = usePortageContactOptions(portageCompanyId)

  const options = useMemo<ContactOption[]>(
    () =>
      contacts.map((contact) => ({
        value: contact.id,
        label: formatPortageContactLabel(contact),
        email: contact.email,
        status: contact.status,
        searchText: `${contact.first_name || ''} ${contact.last_name || ''} ${contact.email}`.toLowerCase(),
      })),
    [contacts]
  )

  const selectedOption = options.find((opt) => opt.value === value)
  const displayLabel = selectedOption?.label ?? (value ? selectedLabel : null) ?? null

  const handleSelect = (optionValue: string) => {
    const option = options.find((opt) => opt.value === optionValue)
    const isUnset = optionValue === value
    onChange(
      isUnset ? null : optionValue,
      isUnset ? null : option?.label ?? null,
      isUnset ? null : option?.status ?? null
    )
  }

  return (
    <div className="space-y-1">
      <SearchableComboboxShell<ContactOption>
        options={options}
        selectedValue={value}
        onSelect={handleSelect}
        disabled={disabled || !portageCompanyId}
        error={!!error}
        className={className}
        isPlaceholder={!displayLabel}
        triggerContent={
          <span className="truncate">
            {displayLabel || t('freelancerOnboarding.portageContact.placeholder')}
          </span>
        }
        searchPlaceholder={t('freelancerOnboarding.portageContact.searchPlaceholder')}
        isLoading={!!portageCompanyId && isLoading}
        loadingMessage={t('common.loading')}
        emptyMessage={
          !portageCompanyId
            ? t('freelancerOnboarding.portageContact.selectCompanyFirst')
            : t('freelancerOnboarding.portageContact.noContacts')
        }
        noResultsMessage={t('freelancerOnboarding.portageContact.noSearchResults')}
        renderOption={(option) => (
          <>
            <div className="text-sm truncate">{option.label}</div>
            <div className="text-xs text-muted-foreground truncate">{option.email}</div>
          </>
        )}
        renderFooter={
          allowAddContact && portageCompanyId
            ? (close) => (
                <div className="border-t p-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-sm font-medium text-[#20766F] hover:bg-[#20766F]/10 hover:text-[#20766F]"
                    onClick={() => {
                      close()
                      if (onAddContactClick) {
                        onAddContactClick()
                      } else {
                        setIsSuggestOpen(true)
                      }
                    }}
                  >
                    <FooterIcon className="h-4 w-4 shrink-0" />
                    {footerLabel}
                  </Button>
                </div>
              )
            : undefined
        }
      />
      {error && <p className="text-sm text-red-500">{error}</p>}

      {ownsDialog && (
        <PortageContactSuggestDialog
          open={isSuggestOpen}
          onOpenChange={setIsSuggestOpen}
          portageCompanyId={portageCompanyId}
          portageCompanyName={portageCompanyName}
          variant={addContactVariant}
          onSuggested={(contact) => {
            onChange(contact.id, formatPortageContactLabel(contact), contact.status)
          }}
        />
      )}
    </div>
  )
}
